import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Building2, Calculator, CalendarCheck, Wand2, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { COMPANY, PHONE_DISPLAY, PHONE_TEL, SITE_URL, track, scrollToId } from "@/lib/api";

const META_TITLE = "Kontakt — Zateplenia.eu (RESTOB s.r.o.) | Zateplenie fasád";
const META_DESC = "Kontaktujte Zateplenia.eu — RESTOB s.r.o., Kopčianska 10, 851 01 Bratislava. Telefón 0907 649 964, info@zateplenia.eu. Obhliadky a realizácie zateplenia po celom Slovensku.";

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

export default function Kontakt() {
  useEffect(() => {
    document.title = META_TITLE;
    setMeta("description", META_DESC);
    setCanonical(`${SITE_URL}/kontakt`);
    track("visit", "kontakt");
    const ld = {
      "@context": "https://schema.org",
      "@type": "HomeAndConstructionBusiness",
      "name": "Zateplenia.eu",
      "legalName": COMPANY.name,
      "url": SITE_URL,
      "telephone": PHONE_TEL,
      "email": COMPANY.email,
      "vatID": COMPANY.icdph,
      "taxID": COMPANY.ico,
      "foundingDate": "2006",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": COMPANY.street,
        "addressLocality": "Bratislava",
        "postalCode": "851 01",
        "addressCountry": "SK",
      },
      "areaServed": { "@type": "Country", "name": "Slovensko" },
    };
    let s = document.getElementById("ld-json");
    if (!s) { s = document.createElement("script"); s.id = "ld-json"; s.type = "application/ld+json"; document.head.appendChild(s); }
    s.textContent = JSON.stringify(ld);
  }, []);

  return (
    <div className="bg-ink min-h-screen" data-testid="kontakt-page">
      <Header />

      <section className="pt-36 pb-16">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <div className="inline-flex items-center gap-2 text-gold font-mono2 uppercase tracking-[0.3em] text-xs mb-6">
            <Phone className="w-4 h-4" /> Kontakt
          </div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-display text-white leading-[0.95] text-4xl sm:text-5xl lg:text-6xl" data-testid="kontakt-title">
            Ozvite sa nám —<br /><span className="text-gold">poradíme zdarma</span>
          </motion.h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mt-6 leading-relaxed">
            Obhliadky, cenové ponuky aj realizácie zabezpečujeme po celom Slovensku.
            Najrýchlejšie nás zastihnete telefonicky alebo cez online kalkulačku.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
            <div className="border border-gold/30 bg-gold/5 p-7" data-testid="kontakt-direct">
              <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Priamy kontakt</p>
              <a href={`tel:${PHONE_TEL}`} data-testid="kontakt-phone"
                className="flex items-center gap-4 group py-2">
                <span className="w-11 h-11 border border-gold/40 flex items-center justify-center shrink-0 group-hover:bg-gold/10 transition-colors"><Phone className="w-5 h-5 text-gold" /></span>
                <span>
                  <span className="block text-white/50 text-xs uppercase tracking-widest font-mono2">Telefón</span>
                  <span className="text-white text-xl font-bold group-hover:text-gold transition-colors">{PHONE_DISPLAY}</span>
                </span>
              </a>
              <a href={`mailto:${COMPANY.email}`} data-testid="kontakt-email"
                className="flex items-center gap-4 group py-2 mt-2">
                <span className="w-11 h-11 border border-gold/40 flex items-center justify-center shrink-0 group-hover:bg-gold/10 transition-colors"><Mail className="w-5 h-5 text-gold" /></span>
                <span>
                  <span className="block text-white/50 text-xs uppercase tracking-widest font-mono2">E-mail</span>
                  <span className="text-white text-xl font-bold group-hover:text-gold transition-colors">{COMPANY.email}</span>
                </span>
              </a>
              <a href="https://www.google.com/maps/search/?api=1&query=Kop%C4%8Dianska+10%2C+851+01+Bratislava" target="_blank" rel="noopener noreferrer" data-testid="kontakt-maps-link"
                className="flex items-center gap-4 group py-2 mt-2">
                <span className="w-11 h-11 border border-gold/40 flex items-center justify-center shrink-0 group-hover:bg-gold/10 transition-colors"><MapPin className="w-5 h-5 text-gold" /></span>
                <span>
                  <span className="block text-white/50 text-xs uppercase tracking-widest font-mono2">Sídlo</span>
                  <span className="text-white font-semibold group-hover:text-gold transition-colors inline-flex items-center gap-1.5">Kopčianska 10, 851 01 Bratislava <ExternalLink className="w-3.5 h-3.5 text-white/40" /></span>
                </span>
              </a>
            </div>

            <div className="border border-white/10 bg-ink-2 p-7" data-testid="kontakt-company">
              <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5 flex items-center gap-2"><Building2 className="w-4 h-4" /> Firemné údaje</p>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between gap-4 border-b border-white/5 pb-3"><dt className="text-white/50">Obchodné meno</dt><dd className="text-white font-semibold text-right">{COMPANY.name}</dd></div>
                <div className="flex justify-between gap-4 border-b border-white/5 pb-3"><dt className="text-white/50">IČO</dt><dd className="text-white font-mono2">{COMPANY.ico}</dd></div>
                <div className="flex justify-between gap-4 border-b border-white/5 pb-3"><dt className="text-white/50">DIČ</dt><dd className="text-white font-mono2">{COMPANY.dic}</dd></div>
                <div className="flex justify-between gap-4 border-b border-white/5 pb-3"><dt className="text-white/50">IČ DPH</dt><dd className="text-white font-mono2">{COMPANY.icdph}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-white/50">Zapísaná</dt><dd className="text-white text-right">{COMPANY.court}, odd. {COMPANY.section}, vl. {COMPANY.insert}</dd></div>
              </dl>
              <p className="text-white/40 text-xs mt-6 leading-relaxed">Pôsobíme na trhu od roku 2006 — vyše 350 000 m² zateplených fasád po celom Slovensku.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            <Link to="/#kalkulacka" onClick={() => setTimeout(() => scrollToId("#kalkulacka"), 60)} data-testid="kontakt-cta-calc"
              className="bg-gold text-ink px-6 py-5 font-bold uppercase tracking-wider hover:bg-gold-light transition-colors flex items-center justify-center gap-2 text-sm">
              <Calculator className="w-4 h-4" /> Kalkulačka cien
            </Link>
            <Link to="/objednavka" data-testid="kontakt-cta-order"
              className="border border-white/25 text-white px-6 py-5 font-bold uppercase tracking-wider hover:border-gold hover:text-gold transition-colors flex items-center justify-center gap-2 text-sm">
              <CalendarCheck className="w-4 h-4" /> Cenová ponuka
            </Link>
            <Link to="/vizualizacia" data-testid="kontakt-cta-viz"
              className="border border-white/25 text-white px-6 py-5 font-bold uppercase tracking-wider hover:border-gold hover:text-gold transition-colors flex items-center justify-center gap-2 text-sm">
              <Wand2 className="w-4 h-4" /> AI vizualizácia
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
